import { create } from 'zustand';

/**
 * Global conference UI state. The conference lives OUTSIDE any one page (mounted
 * at the dashboard root) so it survives navigation and can be minimized to a
 * floating window while the room stays live — Teams-style.
 */
interface ConferenceUiState {
  /** The full modal is mounted and visible. */
  isOpen: boolean;
  /** Collapsed to the floating mini-window; the room stays live. */
  minimized: boolean;
  /** The active room id, set once a conference has started. */
  conferenceId: string | null;
  /**
   * Epoch ms when the initiator kicked off a start. While this is recent, the
   * softphone treats an inbound leg as the initiator's own auto-join leg even if
   * Telnyx didn't surface the SIP tag — so their own leg never rings as an
   * incoming call. Null once disarmed / expired.
   */
  armedAt: number | null;
  /**
   * Team members this tab has asked the service to ring in the current call,
   * at the start or added later. The service dials them one after another, so
   * this is how the call knows whether everyone has had their turn before
   * deciding nobody could be reached.
   */
  invitedUserIds: string[];

  /** Open the start screen (member picker). */
  open: () => void;
  /** Record the room id once the conference has started. */
  setConferenceId: (id: string | null) => void;
  /** Record who a newly started call is ringing. */
  setInvitedUserIds: (userIds: string[]) => void;
  /** Record someone added to the live call. */
  addInvitedUserId: (userId: string) => void;
  /** Collapse to the floating window; the room stays live. */
  minimize: () => void;
  /** Expand back to the full modal. */
  restore: () => void;
  /** Fully dismiss (after the room ended / the user left). */
  close: () => void;
  /** Arm auto-join suppression for the initiator's own leg. */
  arm: () => void;
  /** Disarm once the leg has joined (or on teardown). */
  disarm: () => void;
}

export const useConferenceStore = create<ConferenceUiState>((set) => ({
  isOpen: false,
  minimized: false,
  conferenceId: null,
  armedAt: null,
  invitedUserIds: [],

  open: () => set({ isOpen: true, minimized: false }),
  setConferenceId: (conferenceId) => set({ conferenceId }),
  setInvitedUserIds: (invitedUserIds) => set({ invitedUserIds: [...new Set(invitedUserIds)] }),
  addInvitedUserId: (userId) =>
    set((s) =>
      s.invitedUserIds.includes(userId) ? s : { invitedUserIds: [...s.invitedUserIds, userId] },
    ),
  minimize: () => set({ minimized: true, isOpen: false }),
  restore: () => set({ minimized: false, isOpen: true }),
  close: () =>
    set({ isOpen: false, minimized: false, conferenceId: null, armedAt: null, invitedUserIds: [] }),
  arm: () => set({ armedAt: Date.now() }),
  disarm: () => set({ armedAt: null }),
}));

/** How long after arming an inbound leg is still assumed to be the auto-join leg. */
const AUTOJOIN_ARM_WINDOW_MS = 30_000;

/**
 * True while the initiator is starting/holding a conference — used by the
 * softphone to auto-answer their own leg silently and by the inbound-call banner
 * to stay hidden. Belt-and-suspenders alongside the SIP auto-join tag.
 */
export function isConferenceLegExpected(): boolean {
  const { armedAt } = useConferenceStore.getState();
  return armedAt != null && Date.now() - armedAt < AUTOJOIN_ARM_WINDOW_MS;
}

/** True while a conference modal/mini-window/room is engaged at all. */
export function isConferenceEngaged(): boolean {
  const s = useConferenceStore.getState();
  return s.isOpen || s.minimized || s.conferenceId != null;
}
